import { useMutation, useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useEffectEvent } from 'react'
import { useNavigate } from 'react-router'
import {
  Button,
  Checkbox,
  Combobox,
  FAIcon,
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  H2,
  Input,
  NumberInput,
  Spinner,
} from '@/components'
import { SalaryCodeCombobox } from '@/components/combobox/salary-code-combobox'
import { SalarySettingCombobox } from '@/components/combobox/salary-setting-combobox'
import {
  CREATE_COMPANY_RULE,
  PATCH_COMPANY_RULE,
} from '@/modules/companies/mutations'
import { COMPANY_RULES_QUERY } from '@/modules/companies/queries'
import type {
  CompanyRuleNode,
  CompanyRulePartCapability,
  CompanyRuleTriggerCapability,
} from '@/modules/companies/types.graphql'
import { useBreadcrumbs, useOnErrorAuto, useParam } from '@/utils/hooks'
import { SettingsWrapper } from '../common'
import type {
  CompanyRuleForm as CompanyRuleFormValues,
  CompanyRulePartForm,
  RulePartValue,
} from './company-rule-helpers'
import {
  DEFAULT_RULE_CAPABILITIES,
  companyRuleToForm,
  formToMutationInput,
  getFieldDisplayName,
  getFirstAvailableCapability,
  getRulePartDisplayName,
  isRuleValid,
  isValueValid,
  makePart,
  makeTriggerPart,
} from './company-rule-helpers'

const RULES_LIST_PATH = '/settings/company-rules'

type RulePartKind = 'condition' | 'effect'

interface CompanyRulesQuery {
  currentCompany: {
    id: string
    ruleCapabilities: CompanyRuleTriggerCapability[]
    rules: CompanyRuleNode[]
  } | null
}

function emptyForm(): CompanyRuleFormValues {
  return {
    id: 'new-company-rule',
    name: '',
    active: true,
    trigger: null,
    conditions: [],
    effects: [],
  }
}

/* Presentational editor — owns the rule form state. */

interface CompanyRuleEditorProps {
  initialValues: CompanyRuleFormValues
  capabilities: CompanyRuleTriggerCapability[]
  saving: boolean
  onSave: (form: CompanyRuleFormValues) => void
  onCancel: () => void
}

const CompanyRuleEditor: React.FC<CompanyRuleEditorProps> = ({
  initialValues,
  capabilities,
  saving,
  onSave,
  onCancel,
}) => {
  const translations = useTranslate({
    name: 'common.name',
    active: 'common.active',
    save: 'common.save',
    cancel: 'common.cancel',
    required: 'common.required',
    none: 'common.none',
    trigger: 'settings.rule-trigger',
    selectTrigger: 'settings.rule-select-trigger',
    conditions: 'settings.rule-node-condition',
    effects: 'settings.rule-node-effect',
    selectCondition: 'settings.rule-select-condition',
    selectEffect: 'settings.rule-select-effect',
    addCondition: 'settings.rule-add-condition',
    addEffect: 'settings.rule-add-effect',
    triggerManual: 'settings.rule-trigger-manual-time-entry-salary-calculation',
    conditionSalaryWorkTime: 'settings.rule-condition-salary-work-time',
    conditionSalaryCode: 'settings.rule-condition-salary-code',
    salaryWorkTime: 'settings.rule-condition-salary-work-time',
    salaryCode: 'settings.rule-condition-salary-code',
    effectAddSalaryCodeAmount: 'settings.rule-effect-add-salary-code-amount',
    amount: 'common.amount',
  })

  const {
    formValues,
    formErrors: errors,
    updateForm,
    formChangeHandler: handler,
  } = useForm<CompanyRuleFormValues>({
    values: initialValues,
    validators: {
      name: value => (value ? null : translations.required),
    },
    config: {
      validateImmediately: true,
    },
  })

  const selectedCapability = capabilities.find(
    capability => capability.trigger === formValues.trigger?.type
  )
  const conditionCapabilities = selectedCapability?.conditions ?? []
  const effectCapabilities = selectedCapability?.effects ?? []
  const ruleValid = isRuleValid(formValues, capabilities)

  function getPartCapabilities(kind: RulePartKind) {
    return kind === 'condition' ? conditionCapabilities : effectCapabilities
  }

  function updateTrigger(trigger: string) {
    updateForm({
      trigger: makeTriggerPart(trigger),
      conditions: [],
      effects: [],
    })
  }

  function addPart(kind: RulePartKind) {
    const existing =
      kind === 'condition' ? formValues.conditions : formValues.effects
    const capability = getFirstAvailableCapability(
      getPartCapabilities(kind),
      existing
    )
    if (!capability) return

    const nextPart = makePart(capability.type, capability)
    if (kind === 'condition') {
      updateForm({ conditions: [...formValues.conditions, nextPart] })
    } else {
      updateForm({ effects: [...formValues.effects, nextPart] })
    }
  }

  function updatePart(
    kind: RulePartKind,
    partId: string,
    nextPart: CompanyRulePartForm
  ) {
    if (kind === 'condition') {
      updateForm({
        conditions: formValues.conditions.map(part =>
          part.id === partId ? nextPart : part
        ),
      })
      return
    }

    updateForm({
      effects: formValues.effects.map(part =>
        part.id === partId ? nextPart : part
      ),
    })
  }

  function removePart(kind: RulePartKind, partId: string) {
    if (kind === 'condition') {
      updateForm({
        conditions: formValues.conditions.filter(part => part.id !== partId),
      })
    } else {
      updateForm({
        effects: formValues.effects.filter(part => part.id !== partId),
      })
    }
  }

  function setPartValue(
    kind: RulePartKind,
    part: CompanyRulePartForm,
    key: string,
    value: RulePartValue
  ) {
    updatePart(kind, part.id, {
      ...part,
      values: { ...part.values, [key]: value },
    })
  }

  function renderPartField(
    kind: RulePartKind,
    part: CompanyRulePartForm,
    field: CompanyRulePartCapability['fields'][number]
  ) {
    const value = part.values[field.key]
    const error =
      field.required && !isValueValid(value, field.valueType)
        ? translations.required
        : undefined
    const label = getFieldDisplayName(field.key, translations)

    return (
      <Field key={field.key} data-invalid={!!error}>
        <FieldLabel>{label}</FieldLabel>
        {field.valueType === 'SALARY_WORK_TIME' && (
          <SalarySettingCombobox
            mode="single"
            clearable
            value={(value as string | null) ?? ''}
            onChange={nextValue =>
              setPartValue(kind, part, field.key, nextValue || null)
            }
          />
        )}
        {field.valueType === 'SALARY_CODE' && (
          <SalaryCodeCombobox
            mode="single"
            clearable
            value={(value as string | null) ?? ''}
            onChange={nextValue =>
              setPartValue(kind, part, field.key, nextValue || null)
            }
          />
        )}
        {field.valueType === 'POSITIVE_DECIMAL' && (
          <NumberInput
            value={Number(value ?? 0)}
            min={0}
            decimalScale={2}
            onChange={nextValue =>
              setPartValue(
                kind,
                part,
                field.key,
                typeof nextValue === 'number' ? nextValue : null
              )
            }
          />
        )}
        <FieldError>{error}</FieldError>
      </Field>
    )
  }

  function renderPart(kind: RulePartKind, part: CompanyRulePartForm) {
    const partCapabilities = getPartCapabilities(kind)
    const capability = partCapabilities.find(
      option => option.type === part.type
    )

    return (
      <div
        key={part.id}
        className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 rounded-xl border border-gray-200 bg-white p-4"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field>
            <FieldLabel>
              {kind === 'condition'
                ? translations.conditions
                : translations.effects}
            </FieldLabel>
            <Combobox
              mode="single"
              value={part.type}
              items={partCapabilities.map(option => ({
                value: option.type,
                label: getRulePartDisplayName(option.type, translations),
              }))}
              onChange={nextType => {
                const nextCapability = partCapabilities.find(
                  option => option.type === nextType
                )
                updatePart(kind, part.id, {
                  ...makePart(nextType, nextCapability),
                  id: part.id,
                })
              }}
            />
          </Field>
          {capability?.fields.map(field => renderPartField(kind, part, field))}
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          className="mt-7 text-gray-400 hover:text-red-500"
          onClick={() => removePart(kind, part.id)}
        >
          <FAIcon icon="times" />
        </Button>
      </div>
    )
  }

  function renderPartSection(
    kind: RulePartKind,
    parts: CompanyRulePartForm[],
    title: string,
    addText: string
  ) {
    const partCapabilities = getPartCapabilities(kind)
    return (
      <section className="grid gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-primary">{title}</h3>
          <Button
            variant="outline"
            size="sm"
            disabled={!selectedCapability || partCapabilities.length === 0}
            onClick={() => addPart(kind)}
          >
            <FAIcon icon="plus" />
            {addText}
          </Button>
        </div>
        {parts.length > 0 ? (
          <div className="grid gap-3">
            {parts.map(part => renderPart(kind, part))}
          </div>
        ) : (
          <span className="text-gray-500">{translations.none}</span>
        )}
      </section>
    )
  }

  return (
    <FieldGroup className="gap-7">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
        <Field>
          <FieldLabel>{translations.name}</FieldLabel>
          <Input
            value={formValues.name}
            error={errors.name}
            onChange={handler('name')}
          />
        </Field>
        <Field className="w-auto">
          <FieldLabel>{translations.active}</FieldLabel>
          <span className="flex h-13 items-center">
            <Checkbox
              value={formValues.active}
              onChange={checked => updateForm({ active: checked === true })}
            />
          </span>
        </Field>
      </div>

      <Field>
        <FieldLabel>{translations.trigger}</FieldLabel>
        <Combobox
          mode="single"
          placeholder={translations.selectTrigger}
          value={formValues.trigger?.type ?? ''}
          items={capabilities.map(option => ({
            value: option.trigger,
            label: getRulePartDisplayName(option.trigger, translations),
          }))}
          onChange={updateTrigger}
        />
      </Field>

      {renderPartSection(
        'condition',
        formValues.conditions,
        translations.conditions,
        translations.addCondition
      )}

      {renderPartSection(
        'effect',
        formValues.effects,
        translations.effects,
        translations.addEffect
      )}

      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          size="lg"
          disabled={saving}
          onClick={onCancel}
        >
          {translations.cancel}
        </Button>
        <Button
          size="lg"
          disabled={!ruleValid || saving}
          onClick={() => onSave(formValues)}
        >
          {translations.save}
        </Button>
      </div>
    </FieldGroup>
  )
}

/* Container — loads data, owns mutations and navigation. */

export const CompanyRuleFormView: React.FC = () => {
  const translations = useTranslate({
    create: 'settings.company-rule',
    created: 'settings.company-rule-created',
    updated: 'settings.company-rule-updated',
  })

  const ruleId = useParam('ruleId', true)
  const isEdit = !!ruleId

  const navigate = useNavigate()
  const addToast = useToast()
  const onError = useOnErrorAuto()
  const { setOverride } = useBreadcrumbs()

  const { data, loading } = useQuery<CompanyRulesQuery>(COMPANY_RULES_QUERY)

  const refetchRules = [{ query: COMPANY_RULES_QUERY }]
  const goToList = () => void navigate(RULES_LIST_PATH)

  const [createCompanyRule, { loading: creating }] = useMutation(
    CREATE_COMPANY_RULE,
    {
      awaitRefetchQueries: true,
      refetchQueries: refetchRules,
      onCompleted: () => {
        addToast('success', translations.created)
        goToList()
      },
      onError: error => onError()(error),
    }
  )
  const [patchCompanyRule, { loading: patching }] = useMutation(
    PATCH_COMPANY_RULE,
    {
      awaitRefetchQueries: true,
      refetchQueries: refetchRules,
      onCompleted: () => {
        addToast('success', translations.updated)
        goToList()
      },
      onError: error => onError()(error),
    }
  )

  const capabilities = data?.currentCompany?.ruleCapabilities?.length
    ? data.currentCompany.ruleCapabilities
    : DEFAULT_RULE_CAPABILITIES

  const rule = isEdit
    ? data?.currentCompany?.rules.find(item => item.id === ruleId) ?? null
    : null

  const overrideBreadcrumb = useEffectEvent(() => {
    if (rule) setOverride(rule.id, rule.name)
  })

  useEffect(() => {
    overrideBreadcrumb()
  }, [rule?.id, rule?.name])

  // The rule no longer exists (deleted or invalid id) — return to the list.
  useEffect(() => {
    if (isEdit && !loading && !rule) goToList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, loading, rule])

  function handleSave(form: CompanyRuleFormValues) {
    if (isEdit && rule) {
      void patchCompanyRule({
        variables: { id: rule.id, input: formToMutationInput(form) },
      })
      return
    }

    void createCompanyRule({
      variables: { input: formToMutationInput(form) },
    })
  }

  // Edit mode needs the rule loaded before the form can be initialised.
  if (isEdit && (loading || !rule)) {
    return (
      <SettingsWrapper>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </SettingsWrapper>
    )
  }

  const initialValues = rule ? companyRuleToForm(rule) : emptyForm()
  const title = rule ? rule.name : translations.create

  return (
    <SettingsWrapper grid={{ flow: 'row' }}>
      <div className="p-8 bg-white rounded-xl shadow-card">
        <H2 className="mb-6">{title}</H2>
        <CompanyRuleEditor
          key={ruleId ?? 'create'}
          initialValues={initialValues}
          capabilities={capabilities}
          saving={creating || patching}
          onSave={handleSave}
          onCancel={goToList}
        />
      </div>
    </SettingsWrapper>
  )
}
